import { DetailScopeProvider, ViewModeProvider } from '@kc-console/context';

import SplitDetailPanelProvider from './SplitDetailPanelProvider';

import type { PropsWithChildren } from 'react';

interface DetailPanelProviderProps extends PropsWithChildren {
  scopeKey: string;
  maxTabs?: number;
  pageKey?: string;
  viewMode?: 'standalone' | 'embedded';
}

/**
 * Detail 화면에서 멀티탭 패널을 사용하기 위한 통합 Provider
 *
 * ViewModeProvider, DetailScopeProvider, SplitDetailPanelProvider를 통합하여
 * Detail 화면에 필요한 모든 컨텍스트를 한 번에 제공합니다.
 *
 * Features:
 * - ViewMode 관리 (standalone/embedded)
 * - Detail scope 관리 (scopeKey 기반)
 * - Multi-tab panel 관리 (SplitDetailPanelProvider)
 *
 * @param scopeKey - DetailScopeProvider용 고유 키
 * @param maxTabs - 최대 탭 개수 (기본값: 1)
 * @param pageKey - 디버깅용 페이지 식별자 (선택)
 * @param viewMode - 뷰 모드 설정 (기본값: 'standalone')
 * @param children - 자식 컴포넌트
 *
 * @example
 * ```tsx
 * <DetailPanelProvider
 *   scopeKey="dns-zones"
 *   maxTabs={5}
 *   pageKey="dns-zones"
 *   viewMode="standalone"
 * >
 *   <ZoneDetail />
 * </DetailPanelProvider>
 * ```
 */
export default function DetailPanelProvider({
  scopeKey,
  maxTabs = 1,
  pageKey,
  viewMode = 'standalone',
  children,
}: DetailPanelProviderProps) {
  return (
    <ViewModeProvider viewMode={viewMode}>
      <DetailScopeProvider scopeKey={scopeKey}>
        <SplitDetailPanelProvider maxTabs={maxTabs} pageKey={pageKey}>
          {children}
        </SplitDetailPanelProvider>
      </DetailScopeProvider>
    </ViewModeProvider>
  );
}
